import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { firestore } from '../../firebase/config';
import './AvailabilityManager.css';

const AvailabilityManagerEvolved = () => {
  const { userProfile } = useContext(AuthContext);
  const [availabilities, setAvailabilities] = useState([]);
  const [specificAvailabilities, setSpecificAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [multiSelectTxt, setMultiSelectTxt] = useState('Séléctionner plusieurs jours');
  const [selectedDate, setSelectedDate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Nouveau state pour le mode d'affichage
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' ou 'specific'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // États pour la sélection multiple
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]); // Dates sélectionnées pour édition multiple
  const [sourceDate, setSourceDate] = useState(null); // Date source pour duplication
  
  // États pour la duplication (conservés pour compatibilité)
  const [duplicationMode, setDuplicationMode] = useState('none');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedDatesForDuplication, setSelectedDatesForDuplication] = useState([]);
  const [isDuplicating, setIsDuplicating] = useState(false);
  
  // État du formulaire
  const [formData, setFormData] = useState({
    startTime: '09:00',
    endTime: '18:00',
    lunchBreakStart: '12:00',
    lunchBreakEnd: '13:30',
    isWorkingDay: true,
    hasLunchBreak: true,
    remoteOnly: false,
    allowsRemote: true,
  });
  
  // Jours de la semaine
  const weekDays = [
    { id: 0, name: 'Lundi' },
    { id: 1, name: 'Mardi' },
    { id: 2, name: 'Mercredi' },
    { id: 3, name: 'Jeudi' },
    { id: 4, name: 'Vendredi' },
    { id: 5, name: 'Samedi' },
    { id: 6, name: 'Dimanche' }
  ];

  // Mois en français
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Charger les disponibilités hebdomadaires ET spécifiques
  useEffect(() => {
    const fetchAvailabilities = async () => {
      if (!userProfile?.practitionerId) return;
      
      setLoading(true);
      
      try {
        console.log("Chargement des disponibilités pour le praticien:", userProfile.practitionerId);
        
        // Récupérer les disponibilités par défaut (hebdomadaires)
        const defaultSnapshot = await firestore
          .collection('availability')
          .where('practitionerId', '==', userProfile.practitionerId)
          .where('type', '==', 'default')
          .get();
        
        // Récupérer les disponibilités spécifiques (par date)
        const specificSnapshot = await firestore
          .collection('availability')
          .where('practitionerId', '==', userProfile.practitionerId)
          .where('type', '==', 'specific')
          .get();
        
        console.log("Disponibilités par défaut trouvées:", defaultSnapshot.size);
        console.log("Disponibilités spécifiques trouvées:", specificSnapshot.size);
        
        // Traiter les disponibilités par défaut
        const availabilityData = {};
        defaultSnapshot.docs.forEach(doc => {
          const data = doc.data();
          availabilityData[data.day] = {
            id: doc.id,
            ...data
          };
        });
        
        // Traiter les disponibilités spécifiques
        const specificData = {};
        specificSnapshot.docs.forEach(doc => {
          const data = doc.data();
          specificData[data.date] = {
            id: doc.id,
            ...data
          };
        });
        
        // Créer les disponibilités par défaut si elles n'existent pas
        const daysNeedingDefaultAvailability = [];
        weekDays.forEach(day => {
          if (!availabilityData[day.id]) {
            daysNeedingDefaultAvailability.push(day.id);
          }
        });
        
        if (daysNeedingDefaultAvailability.length > 0) {
          console.log("Création de disponibilités par défaut pour les jours:", daysNeedingDefaultAvailability);
          
          for (const dayId of daysNeedingDefaultAvailability) {
            const defaultData = {
              practitionerId: userProfile.practitionerId,
              type: 'default',
              day: dayId,
              startTime: '09:00',
              endTime: '18:00',
              lunchBreak: { start: '12:00', end: '13:30' },
              hasLunchBreak: true,
              isWorkingDay: [0, 1, 2, 3, 4].includes(dayId),
              remoteOnly: false,
              allowsRemote: true
            };
            
            try {
              const docRef = await firestore.collection('availability').add(defaultData);
              console.log(`Disponibilité par défaut créée pour le jour ${dayId} avec ID: ${docRef.id}`);
              
              availabilityData[dayId] = {
                id: docRef.id,
                ...defaultData
              };
            } catch (error) {
              console.error(`Erreur lors de la création de la disponibilité par défaut pour le jour ${dayId}:`, error);
            }
          }
        }
        
        // Créer un tableau complet pour tous les jours de la semaine
        const fullAvailabilities = weekDays.map(day => {
          return availabilityData[day.id] || {
            practitionerId: userProfile.practitionerId,
            type: 'default',
            day: day.id,
            startTime: '09:00',
            endTime: '18:00',
            lunchBreak: { start: '12:00', end: '13:30' },
            hasLunchBreak: true,
            isWorkingDay: [0, 1, 2, 3, 4].includes(day.id),
            remoteOnly: false,
            allowsRemote: true
          };
        });
        
        setAvailabilities(fullAvailabilities);
        setSpecificAvailabilities(Object.values(specificData));
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des disponibilités:', error);
        setLoading(false);
      }
    };
    
    fetchAvailabilities();
  }, [userProfile]);

  // Générer le calendrier pour le mois sélectionné
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
    const lastDayOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    const endDate = new Date(lastDayOfMonth);
    
    // Ajuster pour commencer le lundi
    const startDay = startDate.getDay();
    const daysToSubtract = startDay === 0 ? 6 : startDay - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    // Ajuster pour finir le dimanche
    const endDay = endDate.getDay();
    const daysToAdd = endDay === 0 ? 0 : 7 - endDay;
    endDate.setDate(endDate.getDate() + daysToAdd);
    
    const days = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = currentDate.toLocaleDateString('fr-CA');
      const dayOfWeek = currentDate.getDay();
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      
      // Vérifier s'il y a une disponibilité spécifique pour cette date
      const specificAvailability = specificAvailabilities.find(sa => sa.date === dateStr);
      
      // Sinon, utiliser la disponibilité par défaut du jour de la semaine
      const defaultAvailability = availabilities.find(a => a.day === adjustedDayOfWeek);
      
      days.push({
        date: dateStr,
        dayNumber: currentDate.getDate(),
        isCurrentMonth: currentDate.getMonth() === selectedMonth,
        dayOfWeek: adjustedDayOfWeek,
        specificAvailability,
        defaultAvailability,
        hasSpecificSettings: !!specificAvailability
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  // Gestion du clic sur une date du calendrier
  const handleCalendarDayClick = (dayData) => {
    if (multiSelectMode) {
      // Mode sélection multiple
      const dateStr = dayData.date;
      
      if (selectedDates.includes(dateStr)) {
        // Désélectionner la date
        setSelectedDates(selectedDates.filter(d => d !== dateStr));
        
        // Si c'était la date source, la réinitialiser
        if (sourceDate === dateStr) {
          setSourceDate(null);
        }
      } else {
        // Sélectionner la date
        setSelectedDates([...selectedDates, dateStr]);
        
        // Si aucune date source n'est définie et que cette date a une configuration spécifique,
        // la définir comme date source
        if (!sourceDate && dayData.hasSpecificSettings) {
          setSourceDate(dateStr);
        }
      }
    } else {
      // Mode sélection simple (comportement existant)
      handleDaySelect(dayData);
    }
  };

  // Définir une date comme source pour la duplication
  const setAsSourceDate = (dateStr) => {
    setSourceDate(dateStr);
    
    // S'assurer que la date source est aussi sélectionnée
    if (!selectedDates.includes(dateStr)) {
      setSelectedDates([...selectedDates, dateStr]);
    }
  };

  // Activer/Désactiver le mode sélection multiple
  const toggleMultiSelectMode = () => {
    setMultiSelectMode(!multiSelectMode);
    
    // Réinitialiser les sélections en sortant du mode
    if (multiSelectMode) {
      setSelectedDates([]);
      setSourceDate(null);
      setEditMode(false);
      setSelectedDate(null);
    }
  };

  // Annuler la sélection multiple
  const clearMultiSelection = () => {
    setSelectedDates([]);
    setSourceDate(null);
  };

  // Dupliquer la configuration de la date source sur les dates sélectionnées
  const handleMultiDateDuplication = async () => {
    if (!sourceDate || selectedDates.length <= 1) return;
    
    setIsDuplicating(true);
    
    try {
      // Récupérer la configuration de la date source
      const sourceConfig = specificAvailabilities.find(sa => sa.date === sourceDate);
      
      if (!sourceConfig) {
        alert('Aucune configuration trouvée pour la date source');
        return;
      }
      
      const configToSave = {
        practitionerId: userProfile.practitionerId,
        type: 'specific',
        startTime: sourceConfig.startTime,
        endTime: sourceConfig.endTime,
        lunchBreak: sourceConfig.lunchBreak,
        hasLunchBreak: sourceConfig.hasLunchBreak,
        isWorkingDay: sourceConfig.isWorkingDay,
        remoteOnly: sourceConfig.remoteOnly,
        allowsRemote: sourceConfig.allowsRemote
      };
      
      const batch = [];
      const targetDates = selectedDates.filter(date => date !== sourceDate);
      
      for (const targetDate of targetDates) {
        // Vérifier si une configuration existe déjà pour cette date
        const existingConfig = specificAvailabilities.find(sa => sa.date === targetDate);
        
        const configToSaveWithDate = {
          ...configToSave,
          date: targetDate
        };
        
        if (existingConfig && existingConfig.id) {
          // Mettre à jour la configuration existante
          batch.push(
            firestore.collection('availability').doc(existingConfig.id).update(configToSaveWithDate)
          );
        } else {
          // Créer une nouvelle configuration
          batch.push(
            firestore.collection('availability').add(configToSaveWithDate)
          );
        }
      }
      
      await Promise.all(batch);
      
      // Recharger les disponibilités spécifiques
      const specificSnapshot = await firestore
        .collection('availability')
        .where('practitionerId', '==', userProfile.practitionerId)
        .where('type', '==', 'specific')
        .get();
      
      const specificData = specificSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSpecificAvailabilities(specificData);
      
      // Réinitialiser la sélection
      clearMultiSelection();
      setMultiSelectMode(false);
      
      alert(`Configuration dupliquée sur ${targetDates.length} date(s) avec succès !`);
      
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      alert('Erreur lors de la duplication de la configuration');
    } finally {
      setIsDuplicating(false);
    }
  };

  // Appliquer une configuration identique sur toutes les dates sélectionnées
  const handleMultiDateEdit = async () => {
    if (selectedDates.length === 0) return;
    
    setIsDuplicating(true);
    
    try {
      const configToSave = {
        practitionerId: userProfile.practitionerId,
        type: 'specific',
        startTime: formData.startTime,
        endTime: formData.endTime,
        lunchBreak: {
          start: formData.lunchBreakStart,
          end: formData.lunchBreakEnd
        },
        hasLunchBreak: formData.hasLunchBreak,
        isWorkingDay: formData.isWorkingDay,
        remoteOnly: formData.remoteOnly,
        allowsRemote: formData.allowsRemote
      };
      
      const batch = [];
      
      for (const dateStr of selectedDates) {
        // Vérifier si une configuration existe déjà pour cette date
        const existingConfig = specificAvailabilities.find(sa => sa.date === dateStr);
        
        const configToSaveWithDate = {
          ...configToSave,
          date: dateStr
        };
        
        if (existingConfig && existingConfig.id) {
          // Mettre à jour la configuration existante
          batch.push(
            firestore.collection('availability').doc(existingConfig.id).update(configToSaveWithDate)
          );
        } else {
          // Créer une nouvelle configuration
          batch.push(
            firestore.collection('availability').add(configToSaveWithDate)
          );
        }
      }
      
      await Promise.all(batch);
      
      // Recharger les disponibilités spécifiques
      const specificSnapshot = await firestore
        .collection('availability')
        .where('practitionerId', '==', userProfile.practitionerId)
        .where('type', '==', 'specific')
        .get();
      
      const specificData = specificSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSpecificAvailabilities(specificData);
      
      // Réinitialiser
      clearMultiSelection();
      setMultiSelectMode(false);
      setEditMode(false);
      
      alert(`Configuration appliquée sur ${selectedDates.length} date(s) avec succès !`);
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde multiple:', error);
      alert('Erreur lors de la sauvegarde multiple');
    } finally {
      setIsDuplicating(false);
    }
  };

  // Préremplir le formulaire avec les données du jour/date sélectionné(e)
  const handleDaySelect = (dayData) => {
    if (viewMode === 'weekly') {
      // Mode hebdomadaire - sélectionner par jour de la semaine
      setSelectedDay(dayData.day);
      setSelectedDate(null);  
      setMultiSelectTxt("Sélectionner plusieurs jours");    
      setFormData({
        startTime: dayData.startTime || '09:00',
        endTime: dayData.endTime || '18:00',
        lunchBreakStart: dayData.lunchBreak?.start || '12:00',
        lunchBreakEnd: dayData.lunchBreak?.end || '13:30',
        isWorkingDay: dayData.isWorkingDay !== undefined ? dayData.isWorkingDay : true,
        hasLunchBreak: dayData.hasLunchBreak !== undefined ? dayData.hasLunchBreak : true,
        remoteOnly: dayData.remoteOnly || false,
        allowsRemote: dayData.allowsRemote !== undefined ? dayData.allowsRemote : true,
      });
    } else {
      // Mode spécifique - sélectionner par date
      setSelectedDate(dayData.date);
      setSelectedDay(null);

      setMultiSelectTxt(dayData.hasSpecificSettings ? "Dupliquer ce jour" : "Séléctionner plusieurs jours");
      
      // Utiliser la disponibilité spécifique si elle existe, sinon la disponibilité par défaut
      const availability = dayData.specificAvailability || dayData.defaultAvailability;
      
      setFormData({
        startTime: availability?.startTime || '09:00',
        endTime: availability?.endTime || '18:00',
        lunchBreakStart: availability?.lunchBreak?.start || '12:00',
        lunchBreakEnd: availability?.lunchBreak?.end || '13:30',
        isWorkingDay: availability?.isWorkingDay !== undefined ? availability.isWorkingDay : true,
        hasLunchBreak: availability?.hasLunchBreak !== undefined ? availability.hasLunchBreak : true,
        remoteOnly: availability?.remoteOnly || false,
        allowsRemote: availability?.allowsRemote !== undefined ? availability.allowsRemote : true,
      });
    }
    
    setEditMode(true);
  };

  // Mettre à jour les champs du formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'remoteOnly' && checked) {
      setFormData({
        ...formData,
        remoteOnly: true,
        allowsRemote: true
      });
    } else if (name === 'allowsRemote' && !checked) {
      setFormData({
        ...formData,
        allowsRemote: false,
        remoteOnly: false
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Sauvegarder les modifications
  const handleSaveAvailability = async (e) => {
    e.preventDefault();
    
    if (!userProfile?.practitionerId) return;
    
    try {
      const availabilityData = {
        practitionerId: userProfile.practitionerId,
        startTime: formData.startTime,
        endTime: formData.endTime,
        lunchBreak: {
          start: formData.lunchBreakStart,
          end: formData.lunchBreakEnd
        },
        hasLunchBreak: formData.hasLunchBreak,
        isWorkingDay: formData.isWorkingDay,
        remoteOnly: formData.remoteOnly,
        allowsRemote: formData.allowsRemote
      };
      
      if (viewMode === 'weekly' && selectedDay !== null) {
        // Sauvegarder pour un jour de la semaine
        availabilityData.type = 'default';
        availabilityData.day = selectedDay;
        
        const existingAvailability = availabilities.find(a => a.day === selectedDay && a.id);
        
        if (existingAvailability && existingAvailability.id) {
          await firestore
            .collection('availability')
            .doc(existingAvailability.id)
            .update(availabilityData);
        } else {
          const docRef = await firestore
            .collection('availability')
            .add(availabilityData);
          console.log("Nouvelle disponibilité hebdomadaire créée avec ID:", docRef.id);
        }
        
        // Mettre à jour l'état local
        const updatedAvailabilities = availabilities.map(a => {
          if (a.day === selectedDay) {
            return {
              ...a,
              ...availabilityData,
              id: existingAvailability?.id
            };
          }
          return a;
        });
        
        setAvailabilities(updatedAvailabilities);
        
      } else if (viewMode === 'specific' && selectedDate) {
        // Sauvegarder pour une date spécifique
        availabilityData.type = 'specific';
        availabilityData.date = selectedDate;
        
        const existingSpecific = specificAvailabilities.find(sa => sa.date === selectedDate);
        
        if (existingSpecific && existingSpecific.id) {
          await firestore
            .collection('availability')
            .doc(existingSpecific.id)
            .update(availabilityData);
        } else {
          const docRef = await firestore
            .collection('availability')
            .add(availabilityData);
          availabilityData.id = docRef.id;
          console.log("Nouvelle disponibilité spécifique créée avec ID:", docRef.id);
        }
        
        // Mettre à jour l'état local
        const updatedSpecifics = existingSpecific 
          ? specificAvailabilities.map(sa => sa.date === selectedDate ? { ...sa, ...availabilityData } : sa)
          : [...specificAvailabilities, availabilityData];
        
        setSpecificAvailabilities(updatedSpecifics);
      }
      
      setEditMode(false);
      setSelectedDay(null);
      setSelectedDate(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des disponibilités:', error);
      alert(`Erreur lors de la sauvegarde: ${error.message}`);
    }
  };

  // Supprimer une disponibilité spécifique
  const handleDeleteSpecificAvailability = async (dateStr) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette disponibilité spécifique ? Les réglages par défaut du jour de la semaine seront utilisés.')) {
      return;
    }
    
    try {
      const specificToDelete = specificAvailabilities.find(sa => sa.date === dateStr);
      
      if (specificToDelete && specificToDelete.id) {
        await firestore
          .collection('availability')
          .doc(specificToDelete.id)
          .delete();
        
        // Mettre à jour l'état local
        setSpecificAvailabilities(specificAvailabilities.filter(sa => sa.date !== dateStr));
        
        // Si c'était la date sélectionnée, annuler l'édition
        if (selectedDate === dateStr) {
          setEditMode(false);
          setSelectedDate(null);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedDay(null);
    setSelectedDate(null);
  };

  // Navigation mois précédent/suivant
  const handleMonthChange = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  // Générer les dates pour la duplication (conservé pour compatibilité)
  const generateDuplicationDates = () => {
    if (!selectedDate) return [];
    
    const currentDate = new Date(selectedDate);
    const currentDayOfWeek = currentDate.getDay();
    const dates = [];
    
    if (duplicationMode === 'next-week') {
      // Prochaines 4 semaines, même jour de la semaine
      for (let i = 1; i <= 4; i++) {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() + (7 * i));
        dates.push(newDate.toISOString().split('T')[0]);
      }
    } else if (duplicationMode === 'next-month') {
      // Prochains 3 mois, même jour de la semaine
      for (let month = 1; month <= 3; month++) {
        const targetMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + month, 1);
        
        // Trouver tous les jours du mois qui correspondent au même jour de la semaine
        const daysInMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0).getDate();
        
        for (let day = 1; day <= daysInMonth; day++) {
          const testDate = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), day);
          if (testDate.getDay() === currentDayOfWeek) {
            dates.push(testDate.toISOString().split('T')[0]);
          }
        }
      }
    } else if (duplicationMode === 'custom' && customStartDate && customEndDate) {
      // Plage personnalisée, même jour de la semaine
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      
      let checkDate = new Date(startDate);
      while (checkDate <= endDate) {
        if (checkDate.getDay() === currentDayOfWeek && checkDate.toISOString().split('T')[0] !== selectedDate) {
          dates.push(checkDate.toISOString().split('T')[0]);
        }
        checkDate.setDate(checkDate.getDate() + 1);
      }
    }
    
    return dates.filter(date => date !== selectedDate); // Exclure la date source
  };

  // Mettre à jour les dates sélectionnées quand les paramètres changent
  useEffect(() => {
    if (duplicationMode !== 'none' && selectedDate) {
      setSelectedDatesForDuplication(generateDuplicationDates());
    } else {
      setSelectedDatesForDuplication([]);
    }
  }, [duplicationMode, customStartDate, customEndDate, selectedDate]);

  // Dupliquer la configuration sur les dates sélectionnées (conservé pour compatibilité)
  const handleDuplicateConfiguration = async () => {
    if (!selectedDate || selectedDatesForDuplication.length === 0) return;
    
    setIsDuplicating(true);
    
    try {
      const sourceConfig = {
        practitionerId: userProfile.practitionerId,
        type: 'specific',
        startTime: formData.startTime,
        endTime: formData.endTime,
        lunchBreak: {
          start: formData.lunchBreakStart,
          end: formData.lunchBreakEnd
        },
        hasLunchBreak: formData.hasLunchBreak,
        isWorkingDay: formData.isWorkingDay,
        remoteOnly: formData.remoteOnly,
        allowsRemote: formData.allowsRemote
      };
      
      const batch = [];
      
      for (const targetDate of selectedDatesForDuplication) {
        // Vérifier si une configuration existe déjà pour cette date
        const existingConfig = specificAvailabilities.find(sa => sa.date === targetDate);
        
        const configToSave = {
          ...sourceConfig,
          date: targetDate
        };
        
        if (existingConfig && existingConfig.id) {
          // Mettre à jour la configuration existante
          batch.push(
            firestore.collection('availability').doc(existingConfig.id).update(configToSave)
          );
        } else {
          // Créer une nouvelle configuration
          batch.push(
            firestore.collection('availability').add(configToSave)
          );
        }
      }
      
      await Promise.all(batch);
      
      // Recharger les disponibilités spécifiques
      const specificSnapshot = await firestore
        .collection('availability')
        .where('practitionerId', '==', userProfile.practitionerId)
        .where('type', '==', 'specific')
        .get();
      
      const specificData = specificSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSpecificAvailabilities(specificData);
      
      // Réinitialiser la duplication
      setDuplicationMode('none');
      setSelectedDatesForDuplication([]);
      setCustomStartDate('');
      setCustomEndDate('');
      
      alert(`Configuration dupliquée sur ${selectedDatesForDuplication.length} date(s) avec succès !`);
      
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      alert('Erreur lors de la duplication de la configuration');
    } finally {
      setIsDuplicating(false);
    }
  };

  if (loading) {
    return <div className="loading">Chargement des disponibilités...</div>;
  }

  return (
    <div className="availability-manager">
      {/* Sélecteur de mode */}
      <div className="availability-mode-selector">
        <h3>Mode de gestion des disponibilités</h3>
        <div className="mode-buttons">
          <button 
            className={`btn ${viewMode === 'weekly' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setViewMode('weekly')}
          >
            <i className="fas fa-calendar-mng-week"></i> Par jour de la semaine
          </button>
          <button 
            className={`btn ${viewMode === 'specific' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setViewMode('specific')}
          >
            <i className="fas fa-calendar-mng-day"></i> Par date spécifique
          </button>
        </div>
        <p className="mode-description">
          {viewMode === 'weekly' 
            ? "Définissez vos horaires par défaut pour chaque jour de la semaine. Ces horaires seront appliqués de manière récurrente."
            : "Définissez des horaires spécifiques pour des dates particulières. Ces horaires ont la priorité sur les horaires par défaut."
          }
        </p>
      </div>

      {/* Container principal avec formulaire à droite si en mode édition */}
      <div className={`availability-content ${editMode ? 'with-form' : ''}`}>
        
        {/* Vue hebdomadaire */}
        {viewMode === 'weekly' && (
          <div className="availability-list">
            <h3>Vos horaires hebdomadaires</h3>
            <p>Cliquez sur un jour pour modifier les horaires par défaut.</p>
            
            <div className="week-days-list">
              {weekDays.map((day, index) => {
                const availability = availabilities.find(a => a.day === day.id);
                
                return (
                  <div 
                    key={day.id}
                    className={`day-card ${selectedDay === day.id ? 'selected' : ''} 
                               ${!availability?.isWorkingDay ? 'day-off' : ''} 
                               ${availability?.remoteOnly ? 'remote-only-day' : ''}`}
                    onClick={() => handleDaySelect(availability)}
                  >
                    <div className="day-name">{day.name}</div>
                    {availability?.isWorkingDay ? (
                      <div className="day-hours">
                        {availability.startTime} - {availability.endTime}
                        {availability.hasLunchBreak ? (
                          <div className="lunch-break">
                            Pause déjeuner: {availability.lunchBreak?.start} - {availability.lunchBreak?.end}
                          </div>
                        ) : (
                          <div className="no-lunch-break">
                            Sans pause déjeuner
                          </div>
                        )}
                        {availability.remoteOnly ? (
                          <div className="remote-only-indicator">
                            À distance uniquement
                          </div>
                        ) : availability.allowsRemote ? (
                          <div className="allows-remote-indicator">
                            Présentiel & à distance
                          </div>
                        ) : (
                          <div className="no-remote-indicator">
                            Présentiel uniquement
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="day-closed">Fermé</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Vue spécifique par date */}
        {viewMode === 'specific' && (
          <div className="specific-availability-section">
            <div className="calendar-mng-header">
              <button 
                className="btn-icon" 
                onClick={() => handleMonthChange('prev')}
                title="Mois précédent"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <h3>{months[selectedMonth]} {selectedYear}</h3>
              <button 
                className="btn-icon" 
                onClick={() => handleMonthChange('next')}
                title="Mois suivant"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>

            {/* Contrôles de sélection multiple */}
            <div className="multi-select-controls">
              <div className="multi-select-toggle">
                <button
                  className={`btn ${multiSelectMode ? 'btn-primary' : 'btn-outline'}`}
                  onClick={toggleMultiSelectMode}
                >
                  <i style={{marginRight : '5px'}} className={`fas ${multiSelectMode ? 'fa-check-square' : 'fa-square'}`}></i>
                  {multiSelectMode ? 'Désactiver la sélection multiple' :  multiSelectTxt}
                </button>
              </div>

              {multiSelectMode && (
                <div className="multi-select-info">
                  <div className="selection-summary">
                    <span className="selected-count">
                      {selectedDates.length} date(s) sélectionnée(s)
                    </span>
                    {sourceDate && (
                      <span className="source-info">
                        Date source: {new Date(sourceDate).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                  
                  <div className="multi-select-actions">
                    {sourceDate && selectedDates.length > 1 && (
                      <button
                        className="btn btn-primary"
                        onClick={handleMultiDateDuplication}
                        disabled={isDuplicating}
                      >
                        <i className="fas fa-copy"></i>
                        {isDuplicating ? 'Duplication...' : `Dupliquer depuis ${new Date(sourceDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}`}
                      </button>
                    )}
                    
                    {selectedDates.length > 0 && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          // Préremplir le formulaire avec la configuration par défaut
                          setFormData({
                            startTime: '09:00',
                            endTime: '18:00',
                            lunchBreakStart: '12:00',
                            lunchBreakEnd: '13:30',
                            isWorkingDay: true,
                            hasLunchBreak: true,
                            remoteOnly: false,
                            allowsRemote: true,
                          });
                          setEditMode(true);
                        }}
                      >
                        <i className="fas fa-edit"></i>
                        Configurer les {selectedDates.length} date(s)
                      </button>
                    )}
                    
                    <button
                      className="btn btn-outline"
                      onClick={clearMultiSelection}
                    >
                      <i className="fas fa-times"></i>
                      Annuler la sélection
                    </button>
                  </div>
                </div>
              )}
            </div>

            {multiSelectMode && (
              <div className="selection-mode-indicator">
                <i className="fas fa-info-circle"></i>
                Mode sélection multiple actif - Cliquez sur les dates pour les sélectionner
              </div>
            )}

            <div className={`calendar-mng-grid ${multiSelectMode ? 'selection-mode' : ''}`}>
              {/* En-têtes des jours de la semaine */}
              <div className="calendar-mng-weekdays">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                  <div key={day} className="weekday">{day}</div>
                ))}
              </div>
              
              {/* Grille des dates */}
              <div className="calendar-mng-days">
                {generateCalendarDays().map((day, index) => (
                  <div
                    key={index}
                    className={`calendar-mng-day ${!day.isCurrentMonth ? 'other-month' : ''} 
                               ${day.hasSpecificSettings ? 'has-specific' : ''} 
                               ${selectedDate === day.date ? 'selected' : ''}
                               ${selectedDates.includes(day.date) ? 'multi-selected' : ''}
                               ${sourceDate === day.date ? 'source-date' : ''}`}
                    onClick={() => handleCalendarDayClick(day)}
                    title={
                      multiSelectMode
                        ? selectedDates.includes(day.date)
                          ? 'Date sélectionnée - Cliquer pour désélectionner'
                          : 'Cliquer pour sélectionner'
                        : day.hasSpecificSettings 
                          ? 'Horaires spécifiques définis' 
                          : 'Utilise les horaires par défaut'
                    }
                  >
                    <span className="day-number">{day.dayNumber}</span>
                    {day.hasSpecificSettings && !multiSelectMode && (
                      <div className="specific-indicator">
                        <i className="fas fa-star"></i>
                      </div>
                    )}
                    {day.hasSpecificSettings && !multiSelectMode && (
                      <button
                        className="btn-delete-specific"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSpecificAvailability(day.date);
                        }}
                        title="Supprimer les horaires spécifiques"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    
                    {/* Contrôles pour le mode sélection multiple */}
                    {multiSelectMode && day.hasSpecificSettings && (
                      <button
                        className="btn-set-source"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAsSourceDate(day.date);
                        }}
                        title="Définir comme date source pour duplication"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="calendar-mng-legend">
              <div className="legend-item">
                <span className="legend-color default"></span>
                <span>Horaires par défaut</span>
              </div>
              <div className="legend-item">
                <span className="legend-color specific"></span>
                <span>Horaires spécifiques</span>
              </div>
              {multiSelectMode && (
                <>
                  <div className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#fef3c7', borderColor: '#f59e0b'}}></span>
                    <span>Date sélectionnée</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color" style={{backgroundColor: '#10b981', borderColor: '#059669'}}></span>
                    <span>Date source</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Formulaire d'édition - maintenant à droite */}
        {editMode && (selectedDay !== null || selectedDate || selectedDates.length > 0) && (
          <div className="availability-form">
            <h3>
              {viewMode === 'weekly' 
                ? `Modifier les horaires - ${weekDays.find(d => d.id === selectedDay)?.name}`
                : selectedDates.length > 1
                  ? `Configuration pour ${selectedDates.length} dates`
                  : selectedDate
                    ? `Horaires spécifiques - ${new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`
                    : 'Configuration multiple'
              }
            </h3>
            
            {/* Aperçu des dates sélectionnées en mode multiple */}
            {selectedDates.length > 1 && (
              <div className="multi-dates-preview">
                <h4>Dates sélectionnées ({selectedDates.length})</h4>
                <div className="selected-dates-grid">
                  {selectedDates.slice(0, 15).map(date => (
                    <span key={date} className="selected-date-chip">
                      {new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                    </span>
                  ))}
                  {selectedDates.length > 15 && (
                    <span className="selected-date-chip more">
                      +{selectedDates.length - 15} autres...
                    </span>
                  )}
                </div>
              </div>
            )}
            
            <form onSubmit={selectedDates.length > 1 ? (e) => { e.preventDefault(); handleMultiDateEdit(); } : handleSaveAvailability}>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isWorkingDay"
                    checked={formData.isWorkingDay}
                    onChange={handleInputChange}
                  />
                  Jour travaillé
                </label>
              </div>
              
              <div className={`form-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
                <label>Heure d'ouverture</label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                  disabled={!formData.isWorkingDay}
                />
              </div>
              
              <div className={`form-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
                <label>Heure de fermeture</label>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleInputChange}
                  disabled={!formData.isWorkingDay}
                />
              </div>
              
              {/* Section pour les options de rendez-vous à distance */}
              <div className={`form-section remote-options ${!formData.isWorkingDay ? 'disabled' : ''}`}>
                <h4 className="remote-options-title">
                  <i className="fas fa-video"></i> Options de consultation à distance
                </h4>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="allowsRemote"
                      checked={formData.allowsRemote}
                      onChange={handleInputChange}
                      disabled={!formData.isWorkingDay}
                    />
                    Autoriser les rendez-vous à distance
                  </label>
                </div>
                
                {formData.allowsRemote && formData.isWorkingDay && (
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="remoteOnly"
                        checked={formData.remoteOnly}
                        onChange={handleInputChange}
                        disabled={!formData.isWorkingDay || !formData.allowsRemote}
                      />
                      Uniquement des rendez-vous à distance
                    </label>
                    <div className="checkbox-hint">
                      Aucun rendez-vous en présentiel ne sera proposé.
                    </div>
                  </div>
                )}
              </div>
              
              {/* Pause déjeuner */}
              <div className={`form-group checkbox-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
                <label>
                  <input
                    type="checkbox"
                    name="hasLunchBreak"
                    checked={formData.hasLunchBreak}
                    onChange={handleInputChange}
                    disabled={!formData.isWorkingDay}
                  />
                  Pause déjeuner
                </label>
              </div>
              
              {formData.isWorkingDay && formData.hasLunchBreak && (
                <>
                  <div className="form-group-title">Horaires de pause déjeuner</div>
                  
                  <div className="form-row">
                    <div className={`form-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
                      <label>Début</label>
                      <input
                        type="time"
                        name="lunchBreakStart"
                        value={formData.lunchBreakStart}
                        onChange={handleInputChange}
                        disabled={!formData.isWorkingDay || !formData.hasLunchBreak}
                      />
                    </div>
                    
                    <div className={`form-group ${!formData.isWorkingDay ? 'disabled' : ''}`}>
                      <label>Fin</label>
                      <input
                        type="time"
                        name="lunchBreakEnd"
                        value={formData.lunchBreakEnd}
                        onChange={handleInputChange}
                        disabled={!formData.isWorkingDay || !formData.hasLunchBreak}
                      />
                    </div>
                  </div>
                </>
              )}
              
              <div className="form-actions">
                <button type="button" className="btn btn-cancel" onClick={handleCancelEdit}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={isDuplicating}>
                  {isDuplicating 
                    ? 'Enregistrement...' 
                    : selectedDates.length > 1 
                      ? `Appliquer à ${selectedDates.length} dates`
                      : 'Enregistrer'
                  }
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityManagerEvolved;